import React, { useEffect } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { FormControl, Link, Stack, Typography, useTheme } from "@carrotfertility/carotene-core"
import { FormSelect } from "@carrotfertility/carotene-core-x"
import { useWatch } from "react-hook-form"
import { useUserRole } from "components/partner-access/hooks/usePartnerAccess"
import { Locale, SupportedLocale } from "../../../types/Locale"
import { useEnabledLocalesForCurrentUser } from "services/locale/hooks/useGetEnabledLocalesForCurrentUser"

const getLocaleOptionsFromLocaleList = (localeStrings: string[]): { value: string; label: string }[] => {
  return localeStrings.map((localeString) => ({
    value: localeString,
    label: Locale.getDisplayNameFromString(localeString)
  }))
}

interface LocaleSelectorProps {
  locales: string[]
}

const LocaleSelector: React.FC<LocaleSelectorProps> = ({ locales }) => {
  const intl = useIntl()
  const theme = useTheme()
  const inputValue = useWatch({ name: "preferredLocale" })
  const { isPartnerAccessingAccount } = useUserRole()

  const preferredLocaleOptions = getLocaleOptionsFromLocaleList(locales)

  const preferredLocaleMessage = intl.formatMessage({
    defaultMessage: "Preferred Language",
    description: "preferred language on account settings page"
  })

  return (
    <Stack spacing={theme.spacing(theme.tokens.spacing.lg)}>
      <Typography variant="body1">
        <FormattedMessage defaultMessage="Your Carrot app and communications will be in your preferred language." />
      </Typography>
      <FormControl fullWidth>
        <FormSelect
          disabled={isPartnerAccessingAccount}
          label={preferredLocaleMessage}
          name={"preferredLocale"}
          options={preferredLocaleOptions}
          defaultValue={inputValue}
          style={{ textTransform: "capitalize" }}
        />
      </FormControl>
    </Stack>
  )
}

const NoLocaleSupportForCountry: React.FC = () => {
  const theme = useTheme()

  return (
    <Stack spacing={theme.spacing(theme.tokens.spacing.lg)}>
      <Typography variant="body1">
        <FormattedMessage defaultMessage="Language options vary based on where you are employed." />
      </Typography>
      <FormattedMessage
        defaultMessage="<link>Learn more about language availability.</link>"
        values={{
          link: (link) => <Link href="https://get-carrot.zendesk.com/hc/articles/20306918731547">{link}</Link>
        }}
      />
    </Stack>
  )
}

export const LanguageSettings: React.FC = () => {
  const [sortedLocaleStrings, setSortedLocaleStrings] = React.useState<string[]>([
    SupportedLocale.English_UnitedStates.toString()
  ])
  const { data: enabledLocalesForCurrentUser, isLoading } = useEnabledLocalesForCurrentUser()
  const doesCountryHaveLocaleSupport = enabledLocalesForCurrentUser?.length > 1

  useEffect(() => {
    if (enabledLocalesForCurrentUser) {
      setSortedLocaleStrings([
        SupportedLocale.English_UnitedStates.toString(),
        ...enabledLocalesForCurrentUser
          .filter((localeString) => localeString !== SupportedLocale.English_UnitedStates.toString())
          .sort()
      ])
    }
  }, [enabledLocalesForCurrentUser])

  return (
    <Stack>
      <Typography variant="h2">
        <FormattedMessage defaultMessage="Language" />
      </Typography>

      {!isLoading && doesCountryHaveLocaleSupport ? (
        <LocaleSelector locales={sortedLocaleStrings} />
      ) : (
        <NoLocaleSupportForCountry />
      )}
    </Stack>
  )
}
